// ProSystemAlertBanner.jsx
//
// Destination in the deployed repo: src/components/pro/ProSystemAlertBanner.jsx
//
// Mount this inside your main app chrome (App.jsx) for Pro / Command tier
// practices. It renders a banner for the most recent unacknowledged open
// system alert, and lets Owners / Managers dismiss.
//
// Data sources:
//   - the shell's one read (shell_counts, key system_alerts), refreshed every
//     60s by Layout (round 7 #2: this banner had its own 2-minute timer that
//     kept running in hidden tabs); listOpenSystemAlerts(practiceId) when that
//     answer has none (before the migration is applied)
//   - acknowledgeSystemAlert(alertId)

import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { C } from "../../lib/tokens";
import { listOpenSystemAlerts, acknowledgeSystemAlert } from "../../lib/chartPrepApi";
import { queryClient } from "../../lib/queryClient";
import { readShellKey, shellCountKey, SHELL_POLL_MS } from "../../lib/shellCounts";

function severityColors(severity) {
  const s = (severity || "").toLowerCase();
  if (s === "critical") return { bg: C.dangerBg, border: C.dangerSolid, text: C.dangerFg };
  if (s === "warning")  return { bg: C.warnBg, border: C.warnSolid, text: "#78350f" };
  return { bg: C.infoBg, border: C.infoSolid, text: C.infoFg };
}

export default function ProSystemAlertBanner({ practiceId, role, tier }) {
  const [dismissing, setDismissing] = useState(false);

  // Only Pro / Command practices see this, and only Owner / Manager can dismiss.
  const isEligibleTier = tier === "Pro" || tier === "Command";
  const canDismiss = role === "Owner" || role === "Manager";

  const key = shellCountKey("system_alerts", practiceId);
  const q = useQuery({
    queryKey: key,
    queryFn: () => readShellKey("system_alerts", () => listOpenSystemAlerts(practiceId)),
    enabled: !!practiceId && isEligibleTier,
    staleTime: SHELL_POLL_MS,
    retry: 0,
  });
  useEffect(() => {
    // Fail silently - a broken banner should never block the rest of the app.
    // eslint-disable-next-line no-console
    if (q.error) console.error("[ProSystemAlertBanner]", q.error.message || q.error);
  }, [q.error]);
  const alerts = Array.isArray(q.data) ? q.data : [];

  if (!isEligibleTier || alerts.length === 0) return null;

  const alert = alerts[0]; // show the most recent
  const colors = severityColors(alert.severity);

  const handleDismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      await acknowledgeSystemAlert(alert.id);
      queryClient.setQueryData(key, (prev) => (Array.isArray(prev) ? prev.filter((a) => a.id !== alert.id) : prev));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ProSystemAlertBanner] dismiss failed", err.message || err);
    } finally {
      setDismissing(false);
    }
  };

  const hiddenCount = alerts.length - 1;

  return (
    <div
      style={{
        backgroundColor: colors.bg,
        borderBottom: "2px solid " + colors.border,
        color: colors.text,
        padding: "10px 20px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        fontSize: 14,
        lineHeight: 1.45,
      }}
    >
      <div style={{ fontSize: 20, flexShrink: 0 }}>
        {alert.severity === "Critical" ? "\u26A0" : "\u2139"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>
          {alert.title}
          {hiddenCount > 0 && (
            <span style={{ fontWeight: 400, marginLeft: 8, opacity: 0.75 }}>
              (+{hiddenCount} more)
            </span>
          )}
        </div>
        <div style={{ opacity: 0.9, marginTop: 2 }}>
          Ticket <code style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            background: C.hairline,
            padding: "1px 6px",
            borderRadius: 4,
          }}>{alert.ticket_ref}</code>
          {" "}- Contact support@immaculate-consulting.org with this ticket ID.
        </div>
      </div>
      {canDismiss && (
        <button
          onClick={handleDismiss}
          disabled={dismissing}
          style={{
            background: "transparent",
            border: "1px solid " + colors.border,
            color: colors.text,
            padding: "6px 14px",
            borderRadius: 4,
            cursor: dismissing ? "default" : "pointer",
            fontSize: 13,
            fontWeight: 500,
            opacity: dismissing ? 0.6 : 1,
            flexShrink: 0,
          }}
        >
          {dismissing ? "Dismissing..." : "Acknowledge"}
        </button>
      )}
    </div>
  );
}
